import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import { useTesterMode } from '../hooks/useTesterMode';
import DraggableBugFab from './DraggableBugFab';
import './BugReportWidget.css';

const CATEGORIES = [
    { key: 'crash', label: '💥 Краш', points: 50 },
    { key: 'bug', label: '🐛 Баг', points: 30 },
    { key: 'ui', label: '🎨 UI', points: 15 },
    { key: 'translation', label: '🌐 Перевод', points: 10 },
    { key: 'suggestion', label: '💡 Идея', points: 5 },
];

const PRIORITIES = [
    { key: 'low', label: '🟢 Низкий' },
    { key: 'medium', label: '🟡 Средний' },
    { key: 'high', label: '🟠 Высокий' },
    { key: 'critical', label: '🔴 Критичный' },
];

function dataUrlToBlob(dataUrl) {
    const [header, data] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
    return new Blob([array], { type: mime });
}

export default function BugReportWidget() {
    const { t } = useTranslation('bugs');
    const { isTesterMode } = useTesterMode();
    const [isOpen, setIsOpen] = useState(false);
    const [screenshot, setScreenshot] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        title: '',
        description: '',
        category: 'bug',
        priority: 'medium',
    });

    if (!isTesterMode || !localStorage.getItem('token')) return null;

    const currentScreen = window.location.pathname;

    async function takeScreenshot() {
        try {
            const canvas = await html2canvas(document.body, { useCORS: true, logging: false });
            setScreenshot(canvas.toDataURL('image/jpeg', 0.5));
        } catch (e) {
            console.warn('Screenshot failed', e);
        }
    }

    function openForm() {
        setIsOpen(true);
    }

    async function submit() {
        if (!form.title.trim()) {
            alert(t('errorTitle'));
            return;
        }

        const formData = new FormData();
        formData.append('title', form.title);
        formData.append('description', form.description);
        formData.append('category', form.category);
        formData.append('priority', form.priority);
        formData.append('screen', currentScreen);
        formData.append('platform', 'web');
        formData.append('appVersion', process.env.REACT_APP_VERSION || '0.1.0');
        formData.append('deviceInfo', navigator.userAgent);
        formData.append('osVersion', navigator.platform);

        if (screenshot) {
            const blob = dataUrlToBlob(screenshot);
            formData.append('screenshot', blob, 'screenshot.jpg');
        }

        setSubmitting(true);
        try {
            const { data } = await axios.post(`${getApiBaseUrl()}/bugs/report`, formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            alert(`${t('successTitle')}\n${t('successMessage', { points: data.bonusPoints })}`);
            setIsOpen(false);
            setForm({ title: '', description: '', category: 'bug', priority: 'medium' });
            setScreenshot(null);
        } catch {
            alert(t('errorSubmit'));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            {!isOpen && (
                <DraggableBugFab
                    onPress={openForm}
                    title={t('reportTitle')}
                    label="🐛"
                />
            )}

            {isOpen && (
                <div className="bug-modal-overlay" onClick={() => setIsOpen(false)}>
                    <div className="bug-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="bug-modal-header">
                            <strong>🐛 {t('reportTitle')}</strong>
                            <button type="button" onClick={() => setIsOpen(false)} aria-label="Close">✕</button>
                        </div>

                        <div className="bug-modal-body">
                            <p className="bug-form-intro">{t('formIntro')}</p>
                            {screenshot ? (
                                <>
                                    <img src={screenshot} alt="" className="bug-screenshot" />
                                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                                        <button type="button" onClick={takeScreenshot}>🔄 {t('retakeScreenshot')}</button>
                                        <button type="button" onClick={() => setScreenshot(null)}>🗑️ {t('removeScreenshot')}</button>
                                    </div>
                                </>
                            ) : (
                                <button type="button" className="bug-screenshot-add" onClick={takeScreenshot}>
                                    <span>📸 {t('addScreenshot')}</span>
                                    <span className="bug-screenshot-add-hint">{t('addScreenshotHint')}</span>
                                </button>
                            )}

                            <span className="bug-label">{t('category')}</span>
                            <div className="bug-chip-row">
                                {CATEGORIES.map((cat) => (
                                    <button
                                        key={cat.key}
                                        type="button"
                                        className={`bug-chip${form.category === cat.key ? ' active' : ''}`}
                                        onClick={() => setForm({ ...form, category: cat.key })}
                                    >
                                        {cat.label} +{cat.points}
                                    </button>
                                ))}
                            </div>

                            <label className="bug-label">{t('titleLabel')} *</label>
                            <input
                                className="bug-input"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder={t('titlePlaceholder')}
                                maxLength={100}
                            />

                            <label className="bug-label">{t('description')}</label>
                            <textarea
                                className="bug-textarea"
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder={t('descriptionPlaceholder')}
                                maxLength={1000}
                            />

                            <span className="bug-label">{t('priority')}</span>
                            <div className="bug-chip-row">
                                {PRIORITIES.map((p) => (
                                    <button
                                        key={p.key}
                                        type="button"
                                        className={`bug-chip${form.priority === p.key ? ' active' : ''}`}
                                        onClick={() => setForm({ ...form, priority: p.key })}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>

                            <p style={{ fontSize: 12, color: '#999', marginTop: 12 }}>
                                📍 {t('screen')}: {currentScreen}
                            </p>
                        </div>

                        <div className="bug-modal-footer">
                            <button type="button" className="bug-submit" onClick={submit} disabled={submitting}>
                                🚀 {t('submit')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
